import {useAppSelector} from '@/redux-store/store';
import {PhotoCamera} from '@mui/icons-material';
import {
  Button,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Grid,
  IconButton,
  Radio,
  RadioGroup,
  TextField,
  Typography,
  Box
} from '@mui/material';
import {Form, Formik} from 'formik';
import {useCallback, useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import CompanyValidationSchema, {
  EMAIL_MAX_LENGTH,
  URL_MAX_LENGTH
} from '@/schemas/company';
import {TITLE_MAX_LENGTH} from '@/schemas/company';
import {WELCOME_MAX_LENGTH} from '@/schemas/company';
import {IMPRINT_MAX_LENGTH} from '@/schemas/company';
import {
  resetCompanyFormData,
  setCompanyData,
  setCompanyFormData
} from '@/redux-store/slices/company';
import {useDispatch} from 'react-redux';
import {updateCompanyData} from '@/rest/company/update-company-infos';
import LoadingScreen from '@/pages/loading-screen';
import {getCompanyInfos} from '@/rest/company/get-company-infos';
import {Company} from '@/types/company';
import {SupportedLanguage} from '@/types/common';
import TypographyTitle from '@/components/ui/typography-title';
import {
  AdminCompaniesFormValues,
  defaultCompanyInfo,
  resetCompaniesEditFormDataForCompany,
  setCompaniesEditFormData,
  updateCompanyinRedux
} from '@/redux-store/slices/admin/companies';
import {SportSelectInput} from '../../input/sports';

interface CompanyInfosEditFormProps {
  onCancel?: () => void;
  onSubmit?: (updatedData: Company) => void;
  onError?: (error: Error) => void;
  showOnlyTwoButtons?: boolean;
  updatingAdminCompany?: boolean;
  companyId?: string;
}

const CompanyInfosEditForm: React.FC<CompanyInfosEditFormProps> = ({
  onCancel,
  onError,
  onSubmit,
  showOnlyTwoButtons,
  updatingAdminCompany,
  companyId
}) => {
  const dispatch = useDispatch();

  const mainCompanyData = useAppSelector((state) => state.company.company);
  const companyData = useAppSelector(
    (state) => state.adminCompanies.adminCompanies
  );
  const selectedAdminCompany =
    (companyData.find(
      (company) => company._id === companyId
    ) as AdminCompaniesFormValues) || defaultCompanyInfo;

  const adminCompaniesEditForm = useAppSelector((state) =>
    companyId
      ? state.adminCompanies.adminCompaniesCompanyEditForms[companyId]
      : null
  );

  const mainCompanyReduxInitialValues = useAppSelector(
    (state) => state.company.companyForm
  );

  const {t} = useTranslation();

  const initialValues = updatingAdminCompany
    ? adminCompaniesEditForm || defaultCompanyInfo
    : mainCompanyReduxInitialValues;

  const [fileSizeError, setFileSizeError] = useState<string | null>(null);
  const [titleLength, setTitleLength] = useState(0);
  const [emailLength, setEmailLength] = useState(0);
  const [welcomeLength, setWelcomeLength] = useState(0);
  const [urlLength, setUrlLength] = useState(0);
  const [imprintLengthDE, setImprintLengthDE] = useState(
    initialValues.imprint.labels.de.length
  );
  const [imprintLengthEN, setImprintLengthEN] = useState(
    initialValues.imprint.labels.en.length
  );

  const ValidationSchema = CompanyValidationSchema(t);

  const handleLogoChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      if (event.target.files && event.target.files.length > 0) {
        const file = event.target.files[0];
        const fileSize = file.size / 1024 / 1024;
        if (fileSize > 0.5) {
          setFileSizeError(t('file_size_error', {maxSize: 500, unit: 'kB'}));
          return;
        }
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = () => {
          setFileSizeError(null);

          if (!updatingAdminCompany) {
            dispatch(
              setCompanyFormData({
                logo: reader.result as string,
                logoUploaded: true
              })
            );
          } else if (updatingAdminCompany && companyId) {
            dispatch(
              setCompaniesEditFormData({
                _id: companyId,
                formData: {
                  logo: reader.result as string,
                  logoUploaded: true
                }
              })
            );
          }
        };
      }
    },
    [companyId, dispatch, t, updatingAdminCompany]
  );

  const submitForm = useCallback(
    (values: Company) => {
      console.log('values: ', values);
      const {title, welcome, email, imprint, allowedSports} = values;

      let updatedValues: Company = {
        title,
        email,
        welcome,
        imprint,
        allowedSports
      };

      updatedValues = {...updatedValues, logo: initialValues.logo};

      if (initialValues._id) {
        updateCompanyData(updatedValues, initialValues._id)
          .then((response) => {
            if (response) {
              return getCompanyInfos(initialValues._id);
            } else {
              if (onError) {
                onError(new Error('Error while updating company data'));
              }
              throw new Error('No response from updateCompanyData');
            }
          })
          .then((newCompanyValues) => {
            dispatch(setCompanyFormData(newCompanyValues));
            const logoSrc = `${newCompanyValues?.logo}?timestamp=${Date.now()}`;
            if (newCompanyValues) {
              if (!updatingAdminCompany) {
                dispatch(
                  setCompanyData({
                    logo: logoSrc
                  })
                );
              } else if (updatingAdminCompany && companyId) {
                dispatch(updateCompanyinRedux({company: newCompanyValues}));
                dispatch(
                  setCompaniesEditFormData({
                    _id: companyId,
                    formData: newCompanyValues
                  })
                );
              }

              if (onSubmit) {
                onSubmit(newCompanyValues);
              }
            }
          })
          .catch((error) => {
            console.error('Error:', error);
            if (onError) {
              onError(error as Error);
            }
          });
      }
    },
    [
      initialValues.logo,
      initialValues._id,
      onError,
      dispatch,
      updatingAdminCompany,
      companyId,
      onSubmit
    ]
  );

  const companyLogoFromReduxAltered = `${
    mainCompanyData.logo
  }?timestamp=${Date.now()}`;

  const adminCompanyLogoFromReduxAltered = `${
    selectedAdminCompany.logo
  }?timestamp=${Date.now()}`;

  const resetCounters = useCallback(() => {
    if (initialValues) {
      setTitleLength(initialValues.title.length);
      setEmailLength(initialValues.email.length);
      setWelcomeLength(initialValues.welcome.length);
      setUrlLength(initialValues.imprint.url.length);
      setImprintLengthDE(initialValues.imprint.labels.de.length);
      setImprintLengthEN(initialValues.imprint.labels.en.length);
    }
  }, [initialValues]);

  const resetFormData = useCallback(() => {
    if (!updatingAdminCompany) {
      dispatch(resetCompanyFormData());
    } else if (updatingAdminCompany && companyId) {
      dispatch(resetCompaniesEditFormDataForCompany(companyId));
    }
    setFileSizeError(null);
    resetCounters();
  }, [companyId, dispatch, resetCounters, updatingAdminCompany]);

  useEffect(() => {
    if (initialValues) {
      setTitleLength(initialValues.title.length);
      setEmailLength(initialValues.email.length);
      setWelcomeLength(initialValues.welcome.length);
      setUrlLength(initialValues.imprint.url.length);
      setImprintLengthDE(initialValues.imprint.labels.de.length);
      setImprintLengthEN(initialValues.imprint.labels.en.length);
    }
  }, [initialValues, initialValues.labelLanguage]);

  if (!initialValues._id) {
    return <LoadingScreen />;
  }

  return (
    <>
      <Formik
        initialValues={initialValues}
        validationSchema={ValidationSchema}
        validateOnMount={true}
        onSubmit={(values) => {
          submitForm(values);
        }}
      >
        {({
          values,
          errors,
          touched,
          handleChange,
          handleBlur,
          resetForm,

          validateForm,
          setFieldValue
        }) => {
          // console.log(errors);
          // console.log('values: ', values);
          return (
            <Form>
              <TypographyTitle textKey="update_company_data" />
              <Grid container spacing={2} my={1} marginTop={1}>
                <Grid item xs={12}>
                  <TextField
                    name="title"
                    value={values.title}
                    label={t('title')}
                    onChange={(e) => {
                      handleChange(e);
                      setTitleLength(e.target.value.length);
                      if (!updatingAdminCompany) {
                        dispatch(
                          setCompanyFormData({
                            title: e.target.value
                          })
                        );
                      }
                      if (updatingAdminCompany && companyId) {
                        dispatch(
                          setCompaniesEditFormData({
                            _id: companyId,
                            formData: {
                              title: e.target.value
                            }
                          })
                        );
                      }
                    }}
                    onBlur={handleBlur}
                    fullWidth
                    error={touched.title && Boolean(errors.title)}
                    helperText={touched.title && errors.title}
                  />
                  <FormHelperText>
                    {titleLength} / {TITLE_MAX_LENGTH}
                  </FormHelperText>
                </Grid>
              </Grid>

              <Grid container spacing={2} my={1} marginTop={3}>
                <Grid item xs={12}>
                  <TextField
                    name="email"
                    value={values.email}
                    label={t('email')}
                    onChange={(e) => {
                      handleChange(e);
                      setEmailLength(e.target.value.length);
                      if (!updatingAdminCompany) {
                        dispatch(
                          setCompanyFormData({
                            email: e.target.value
                          })
                        );
                      }
                      if (updatingAdminCompany && companyId) {
                        dispatch(
                          setCompaniesEditFormData({
                            _id: companyId,
                            formData: {
                              email: e.target.value
                            }
                          })
                        );
                      }
                    }}
                    onBlur={handleBlur}
                    fullWidth
                    error={touched.email && Boolean(errors.email)}
                    helperText={touched.email && errors.email}
                  />
                  <FormHelperText>
                    {emailLength} / {EMAIL_MAX_LENGTH}
                  </FormHelperText>
                </Grid>
              </Grid>

              <Grid container spacing={2} marginTop={3}>
                <Grid item xs={12}>
                  <TextField
                    name="welcome"
                    value={values.welcome}
                    label={t('welcome_text')}
                    onChange={(e) => {
                      handleChange(e);
                      setWelcomeLength(e.target.value.length);
                      if (!updatingAdminCompany) {
                        dispatch(
                          setCompanyFormData({
                            welcome: e.target.value
                          })
                        );
                      }

                      if (updatingAdminCompany && companyId) {
                        dispatch(
                          setCompaniesEditFormData({
                            _id: companyId,
                            formData: {
                              welcome: e.target.value
                            }
                          })
                        );
                      }
                    }}
                    onBlur={handleBlur}
                    fullWidth
                    multiline
                    rows={4}
                    error={touched.welcome && Boolean(errors.welcome)}
                    helperText={touched.welcome && errors.welcome}
                  />
                  <FormHelperText>
                    {welcomeLength} / {WELCOME_MAX_LENGTH}
                  </FormHelperText>
                </Grid>
              </Grid>

              <Grid container spacing={0} marginTop={2}>
                <Grid
                  item
                  xs={12}
                  container
                  alignItems="center"
                  justifyContent="center"
                >
                  <Typography
                    variant="subtitle1"
                    marginTop={1}
                    marginBottom={1}
                  >
                    {t('imprint')}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    name="imprint.url"
                    value={values.imprint.url}
                    label={t('url')}
                    onChange={(e) => {
                      handleChange(e);
                      setUrlLength(e.target.value.length);
                      if (!updatingAdminCompany) {
                        dispatch(
                          setCompanyFormData({
                            imprint: {
                              ...values.imprint,
                              url: e.target.value
                            }
                          })
                        );
                      }
                      if (updatingAdminCompany && companyId) {
                        dispatch(
                          setCompaniesEditFormData({
                            _id: companyId,
                            formData: {
                              imprint: {
                                ...values.imprint,
                                url: e.target.value
                              }
                            }
                          })
                        );
                      }
                    }}
                    onBlur={handleBlur}
                    fullWidth
                    error={touched.imprint?.url && Boolean(errors.imprint?.url)}
                    helperText={touched.imprint?.url && errors.imprint?.url}
                  />
                  <FormHelperText>
                    {urlLength} / {URL_MAX_LENGTH}
                  </FormHelperText>
                </Grid>
                <Grid item xs={12} marginTop={3}>
                  <FormControl component="fieldset">
                    <RadioGroup
                      row
                      name="labelLanguage"
                      value={values.labelLanguage || 'de'}
                      onChange={(e) => {
                        const newLanguage = e.target.value as SupportedLanguage;
                        setFieldValue('labelLanguage', newLanguage);
                        if (!updatingAdminCompany) {
                          dispatch(
                            setCompanyFormData({labelLanguage: newLanguage})
                          );
                        }
                        if (updatingAdminCompany && companyId) {
                          dispatch(
                            setCompaniesEditFormData({
                              _id: companyId,
                              formData: {
                                labelLanguage: newLanguage
                              }
                            })
                          );
                        }
                        const length =
                          values.imprint.labels[e.target.value as 'de' | 'en']
                            .length;

                        if (e.target.value === 'de') {
                          setImprintLengthDE(length);
                        } else if (e.target.value === 'en') {
                          setImprintLengthEN(length);
                        }
                      }}
                      style={{marginLeft: '10px'}}
                    >
                      <FormControlLabel
                        value="de"
                        control={<Radio />}
                        label={t('german')}
                      />
                      <FormControlLabel
                        value="en"
                        control={<Radio />}
                        label={t('english')}
                      />
                    </RadioGroup>
                  </FormControl>
                </Grid>
                {values.labelLanguage && (
                  <Grid item xs={12} marginTop={0}>
                    <TextField
                      name={`imprint.labels.${values.labelLanguage}`}
                      value={values.imprint.labels[values.labelLanguage]}
                      label={t('label_company')}
                      onChange={(e) => {
                        handleChange(e);
                        if (values.labelLanguage && !updatingAdminCompany) {
                          dispatch(
                            setCompanyFormData({
                              imprint: {
                                ...values.imprint,
                                labels: {
                                  ...values.imprint.labels,
                                  [values.labelLanguage]: e.target.value
                                }
                              }
                            })
                          );
                        }
                        if (
                          updatingAdminCompany &&
                          values.labelLanguage &&
                          companyId
                        ) {
                          dispatch(
                            setCompaniesEditFormData({
                              _id: companyId,
                              formData: {
                                imprint: {
                                  ...values.imprint,
                                  labels: {
                                    ...values.imprint.labels,
                                    [values.labelLanguage]: e.target.value
                                  }
                                }
                              }
                            })
                          );
                        }

                        if (values.labelLanguage === 'de') {
                          setImprintLengthDE(e.target.value.length);
                        } else if (values.labelLanguage === 'en') {
                          setImprintLengthEN(e.target.value.length);
                        }
                      }}
                      onBlur={handleBlur}
                      fullWidth
                      multiline
                      maxRows={2}
                    />
                    <FormHelperText>
                      {values.labelLanguage === 'de'
                        ? `${imprintLengthDE} / ${IMPRINT_MAX_LENGTH}`
                        : `${imprintLengthEN} / ${IMPRINT_MAX_LENGTH}`}
                    </FormHelperText>
                    <FormHelperText error>
                      {touched.imprint?.labels?.de &&
                        errors.imprint?.labels?.de}
                    </FormHelperText>
                    <FormHelperText error>
                      {touched.imprint?.labels?.en &&
                        errors.imprint?.labels?.en}
                    </FormHelperText>
                  </Grid>
                )}
                {updatingAdminCompany && (
                  <Grid container spacing={2} my={1} marginTop={2}>
                    <Grid item xs={12}>
                      <SportSelectInput
                        name="allowedSports"
                        label="company_sports"
                        value={values.allowedSports}
                        error={errors.allowedSports}
                        touched={touched.allowedSports}
                        onBlur={handleBlur}
                        onChange={(sportIds) => {
                          let sports;
                          if (typeof sportIds === 'string') {
                            sports = [sportIds];
                          } else {
                            sports = sportIds;
                          }
                          setFieldValue('allowedSports', sports);
                          if (updatingAdminCompany && companyId) {
                            dispatch(
                              setCompaniesEditFormData({
                                _id: companyId,
                                formData: {
                                  allowedSports: sports
                                }
                              })
                            );
                          }
                        }}
                        multiple={true}
                        optional
                      />
                    </Grid>
                  </Grid>
                )}
              </Grid>

              <Grid
                container
                spacing={2}
                marginTop={3}
                justifyContent={'center'}
              >
                <Grid
                  item
                  xs={12}
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center'
                  }}
                >
                  <Typography
                    variant="subtitle1"
                    align="center"
                    sx={{marginBottom: 2}}
                  >
                    {updatingAdminCompany ? t('company_logo') : t('your_logo')}
                  </Typography>
                </Grid>

                <Grid
                  item
                  xs={12}
                  sm={6}
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center'
                  }}
                >
                  <label htmlFor="logo-upload">
                    <Button
                      component="span"
                      variant="outlined"
                      color="secondary"
                      startIcon={<PhotoCamera />}
                      sx={{
                        display: {xs: 'none', sm: 'flex'},
                        textTransform: 'none'
                      }}
                    >
                      {t('logo_upload')}
                    </Button>
                  </label>
                  <input
                    id="logo-upload"
                    name="logo"
                    type="file"
                    onChange={handleLogoChange}
                    accept=".jpg, .jpeg, .png"
                    hidden
                  />
                  <label htmlFor="logo-upload-xs">
                    <IconButton
                      aria-label={t('logo_upload')}
                      color="secondary"
                      component="span"
                      sx={{
                        display: {xs: 'flex', sm: 'none'}
                      }}
                    >
                      <PhotoCamera />
                    </IconButton>
                  </label>
                  <input
                    id="logo-upload-xs"
                    name="logo"
                    type="file"
                    onChange={handleLogoChange}
                    accept=".jpg, .jpeg, .png"
                    hidden
                  />
                </Grid>

                <Box
                  component="img"
                  src={
                    updatingAdminCompany
                      ? initialValues.logoUploaded
                        ? initialValues.logo
                        : adminCompanyLogoFromReduxAltered
                      : initialValues.logoUploaded
                      ? initialValues.logo
                      : companyLogoFromReduxAltered
                  }
                  alt={t('company_logo')}
                  sx={{
                    // marginRight: '10px',
                    maxWidth: {xs: '150px', sm: '200px'},
                    maxHeight: '100px',
                    marginTop: {xs: 2, sm: 0}
                  }}
                />
              </Grid>

              {fileSizeError && (
                <Typography align="center" color="error" marginTop={2}>
                  {fileSizeError}
                </Typography>
              )}
              {!companyLogoFromReduxAltered ||
                !adminCompanyLogoFromReduxAltered ||
                (!initialValues.logo && (
                  <Typography align="center" color="error" marginTop={2}>
                    {t('validation:is_required', {name: t('company_logo')})}
                  </Typography>
                ))}

              <Grid
                container
                direction={{xs: 'column-reverse', sm: 'row'}}
                justifyContent="center"
                spacing={2}
                sx={{
                  marginTop: {xs: 2, sm: 5},
                  marginBottom: 5
                }}
              >
                {!showOnlyTwoButtons && (
                  <Grid
                    item
                    xs={12}
                    sm={4}
                    sx={{
                      display: 'flex',
                      justifyContent: 'center',
                      order: {xs: 1, sm: 0}
                    }}
                  >
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={() => {
                        resetFormData();
                        resetForm();
                        if (onCancel) {
                          onCancel();
                        }
                      }}
                      sx={{
                        width: {xs: '50%', sm: '70%'}
                      }}
                    >
                      {t('cancel')}
                    </Button>
                  </Grid>
                )}

                <Grid
                  item
                  xs={12}
                  sm={showOnlyTwoButtons ? 6 : 4}
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    order: {xs: 2, sm: showOnlyTwoButtons ? 1 : 2},
                    marginBottom: {xs: 2, sm: 0}
                  }}
                >
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={() => {
                      resetFormData();
                      resetForm({
                        values: updatingAdminCompany
                          ? {...selectedAdminCompany, labelLanguage: 'de'}
                          : {...mainCompanyData, labelLanguage: 'de'}
                      });

                      setFieldValue('labelLanguage', 'de');
                      validateForm();
                    }}
                    sx={{width: {xs: '50%', sm: '70%'}}}
                  >
                    {t('reset')}
                  </Button>
                </Grid>

                <Grid
                  item
                  xs={12}
                  sm={showOnlyTwoButtons ? 6 : 4}
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    marginBottom: {xs: 2, sm: 0},
                    order: {
                      xs: showOnlyTwoButtons ? 1 : 3,
                      sm: showOnlyTwoButtons ? 2 : 3
                    }
                  }}
                >
                  <Button
                    variant="contained"
                    color="primary"
                    type="submit"
                    disabled={!!fileSizeError}
                    sx={{width: {xs: '50%', sm: '70%'}}}
                  >
                    {t('save')}
                  </Button>
                </Grid>
              </Grid>
            </Form>
          );
        }}
      </Formik>
    </>
  );
};

export default CompanyInfosEditForm;
